/**
 * Утилита для вычисления экспоненциального прогресса по формуле:
 * progress = 1 - exp(-t / INTERVAL)
 *
 * Эта формула обеспечивает:
 * - Прогресс никогда не достигает 100% (только асимптотически приближается)
 * - Постепенное замедление роста прогресса
 * - Плавный переход без зависаний
 */

/**
 * Вычисляет прогресс на основе прошедшего времени
 * @param elapsedSeconds - время, прошедшее с начала текущей стадии (в секундах)
 * @param expectedDuration - ожидаемая продолжительность стадии (в секундах)
 * @returns прогресс от 0 до 1 (не включая 1)
 */
export function calculateExponentialProgress(elapsedSeconds: number, expectedDuration: number): number {
  // progress = 1 - exp(-t / INTERVAL)
  const progress = 1 - Math.exp(-elapsedSeconds / expectedDuration);

  // Ограничиваем прогресс до 0.975 (97.5%), чтобы оставался больший зазор до границы
  // Это предотвращает визуальное "зависание" на 24.9%, 49.9% и т.д.
  return Math.min(progress, 0.975);
}

/**
 * Вычисляет прогресс для всего процесса оптимизации с несколькими стадиями
 * @param currentStage - текущая стадия (0, 1, 2, ..., totalStages-1)
 * @param totalStages - общее количество стадий
 * @param stageProgress - прогресс внутри текущей стадии (0-1)
 * @returns общий прогресс от 0 до 100
 */
export function calculateTotalProgress(currentStage: number, totalStages: number, stageProgress: number): number {
  // Размер одной стадии в процентах
  const stageSize = 100 / totalStages;

  // Прогресс до текущей стадии
  const completedProgress = currentStage * stageSize;

  // Прогресс текущей стадии
  const currentStageProgress = stageProgress * stageSize;

  // Общий прогресс
  const totalProgress = completedProgress + currentStageProgress;

  return Math.min(totalProgress, 100);
}

/**
 * Получает время, прошедшее с начала стадии
 * @param startTime - timestamp начала стадии (в миллисекундах)
 * @returns прошедшее время в секундах
 */
export function getElapsedSeconds(startTime: number): number {
  const now = Date.now();
  const elapsedMs = now - startTime;
  return elapsedMs / 1000;
}

/**
 * Визуализация кривой прогресса для отладки
 * Возвращает массив точек [время, прогресс] для построения графика
 */
export function getProgressCurve(expectedDuration: number, samplesCount: number = 100): Array<[number, number]> {
  const curve: Array<[number, number]> = [];

  for (let i = 0; i <= samplesCount; i++) {
    const t = (i / samplesCount) * expectedDuration * 5; // 5x ожидаемой длительности
    const progress = calculateExponentialProgress(t, expectedDuration);
    curve.push([t, progress]);
  }

  return curve;
}
